import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import Adjective from './Adjective';
import { useEffect, useRef } from "react";
import Helpers from '../Helpers';
import {
   applyChecklistRatingDigit,
   adminDigitToRating,
   formatChecklistRatingDisplay,
   isValidChecklistRating,
   resolveRatingInput,
   shouldAdvanceChecklistRatingEntry,
} from './checklistRating';
import { isPhoneOrTabletDevice } from "@/utils/phoneDevice";

const CHECKLIST_HONEYPOT_ITEM = {
   id: "19",
   name: "responsibility",
   description: "Being accountable for outcomes",
};

function isChecklistHoneypotItem(item) {
   return String(item?.id) === CHECKLIST_HONEYPOT_ITEM.id;
}

function withChecklistHoneypot(items) {
   const source = Array.isArray(items) ? items : [];
   if (source.some(isChecklistHoneypotItem)) {
      return source;
   }
   return [...source, CHECKLIST_HONEYPOT_ITEM];
}

function scoredChecklistItems(items) {
   return (Array.isArray(items) ? items : []).filter((item) => !isChecklistHoneypotItem(item));
}

function checklistRatingInputs(containerEl: HTMLElement) {
   return Array.from(
      containerEl.querySelectorAll('tbody tr:not(.honeypot) input.checklistRatingInput'),
   ) as HTMLInputElement[];
}

export default function Checklist(props) {
   const { sectionData, values } = props;
   const currentSection = sectionData?.currentSection;
   const containerRef = useRef(null);
   const languageContent = Helpers.getSectionContent(currentSection, sectionData?.lang);
   const elements = Helpers.getSectionElements(languageContent);
   const instructionsHtml = Helpers.getSectionHtmlValue(languageContent, "instructions");
   const headers = Array.isArray(elements?.headers) ? elements.headers : [];
   const items = Array.isArray(elements?.items) ? elements.items : [];
   const renderedItems = withChecklistHoneypot(items);
   const ratingInput = resolveRatingInput(elements, {
      fallback: isPhoneOrTabletDevice() ? "dropdown" : "input",
   });

   function getIssueCount(validationValues) {
      let localIssueCount = 0;
      const adjectiveCount = Number(scoredChecklistItems(items).length || 0);
      for (let i = 1; i <= adjectiveCount; i++) {
         const cid = currentSection.formId + i + 'c';
         if (validationValues[cid] === undefined || validationValues[cid] === '')
            localIssueCount++;

         const sid = currentSection.formId + i + 'r';
         const rating = Number(validationValues[sid] || 0);
         if (rating < 1 || rating > 10)
            localIssueCount++;
      }
      return localIssueCount;
   }

   useEffect(() => {
      if (!isOnTab()) return;
      sectionData.registerSection( 767);
   }, [sectionData?.currentSection?.name]);

   useEffect(() => {
      if (!isOnTab() || !props?.sectionData?.isAdmin || !containerRef.current) return;
      const firstInput = containerRef.current.querySelector('input.checklistRatingInput') as HTMLInputElement | null;
      firstInput?.focus();
   }, [sectionData?.currentSection?.name, sectionData?.isAdmin]);

   useEffect(() => {
      if (!isOnTab()) return;
      const nextCount = Number(sectionData?.nextCount || 0);
      const issueCount = nextCount > 0 ? getIssueCount(values || {}) : 0;
      sectionData?.updateIssueCount?.(issueCount);
   }, [items?.length, sectionData?.nextCount, sectionData?.updateIssueCount, values]);

   useEffect(() => {
      if (isOnTab()) {
         // Add event listener for keyboard input
         const handleKeyPress = (e) => {
            if (!props?.sectionData?.isAdmin) return;

            const activeTag = document.activeElement?.tagName?.toLowerCase();
            if (activeTag === "textarea" || activeTag === "select") return;

            if (activeTag === "input" && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
               e.preventDefault();
               return;
            }

            // When a rating input is focused in admin mode, accept one digit then maybe advance
            if (activeTag === "input" && /^[0-9]$/.test(e.key)) {
               e.preventDefault();
               const containerEl = containerRef.current;
               if (!containerEl) return;
               const inputs = checklistRatingInputs(containerEl);
               const currentInput = document.activeElement as HTMLInputElement;
               const currentIndex = inputs.indexOf(currentInput);
               if (currentIndex < 0) return;

               const currentDisplay = currentInput.value || "";
               const numericValue = applyChecklistRatingDigit(currentDisplay, e.key);

               // Set value and trigger React's onChange via native setter
               const nativeValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
               nativeValueSetter?.call(currentInput, formatChecklistRatingDisplay(numericValue));
               currentInput.dispatchEvent(new Event('input', { bubbles: true }));

               // Advance focus unless a lone 1 was entered (may become 10 with the next 0)
               if (shouldAdvanceChecklistRatingEntry(currentDisplay, numericValue)
                  && currentIndex + 1 < inputs.length) {
                  inputs[currentIndex + 1].focus();
                  inputs[currentIndex + 1].select();
               }
               return;
            }

            const key = (e.key || "").toLowerCase();
            const containerEl = containerRef.current;
            if (!containerEl) return;

            if (ratingInput !== "input" && /^[0-9]$/.test(e.key)) {
               e.preventDefault();
               const value = adminDigitToRating(e.key);
               if (value === null) return;

               for (let i = 1; i <= scoredChecklistItems(items).length; i++) {
                  const sid = currentSection.formId + i + "r";
                  const rating = Number(values?.[sid] || 0);
                  if (!isValidChecklistRating(rating)) {
                     void sectionData.onAnswer(String(i) + "r", value);
                     break;
                  }
               }
               return;
            }

            // Digits 1-9 and 0 (0 commits 10)
            if (/^[0-9]$/.test(key)) {
               e.preventDefault();

               const value = applyChecklistRatingDigit("", key);

               // If no input is focused, write into the first unfilled checklist input.
               const numberInputs = checklistRatingInputs(containerEl);
               const firstUnfilledInput = numberInputs.find((input) => {
                  const currentValue = (input.value || '').trim();
                  return currentValue === '';
               });

               if (firstUnfilledInput) {
                  const nativeValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
                  nativeValueSetter?.call(firstUnfilledInput, formatChecklistRatingDisplay(value));
                  firstUnfilledInput.dispatchEvent(new Event('input', { bubbles: true }));
               }
            }
            // Check if it's 't' for Trait or 's' for Skill
            else if (key === 't' || key === 's') {
               e.preventDefault();

               // Find all table rows (skip header)
               const tableRows = containerEl.querySelectorAll('tbody tr:not(.honeypot)');

               // Look for first row without trait/skill selection
               for (let i = 0; i < tableRows.length; i++) {
                  const row = tableRows[i];
                  const cells = row.children;

                  if (cells.length >= 5) {
                     const traitCell = cells[3]; // 4th column (0-indexed)
                     const skillCell = cells[4]; // 5th column (0-indexed)

                     // Check if neither trait nor skill is selected (no 'checked' class)
                     const traitSelected = traitCell.classList.contains('checked');
                     const skillSelected = skillCell.classList.contains('checked');

                     if (!traitSelected && !skillSelected) {
                        // This row needs trait/skill selection
                        const targetCell = key === 't' ? traitCell : skillCell;
                        (targetCell as HTMLElement).click();
                        break;
                     }
                  }
               }

            }
         };

         // Listen at document level so shortcuts still work after focus moves to controls (e.g. Next button).
         document.addEventListener('keydown', handleKeyPress);

         return () => {
            document.removeEventListener('keydown', handleKeyPress);
         };
      }
   }, [sectionData?.currentSection?.name, sectionData?.isAdmin, ratingInput, items?.length, values]);

   function isOnTab() { return currentSection?.formId === 300; }
   
   if(!isOnTab()) return;

   async function onChange(childId, value) {
      await sectionData.onAnswer(childId, value);
   }

   return (

      <div className="adjectives" ref={containerRef} tabIndex={0}>
         {instructionsHtml && (
         <div className="instructions left">{parseSanitizedHtml(instructionsHtml || "")}</div>
         )}
         <div className="SectionBody">

         <table className="tableData nowrap highlightEven">
            <thead>
               <tr>
                  <th className="rating columnheader columndata" data-column="1">{headers[0] || ""}</th>
                  <th className="adjective columnheader columndata" data-column="2">{headers[1] || ""}</th>
                  <th>{headers[2] || ""}</th>
                  <th className="trait columnheader columndata" data-column="4">{headers[3] || ""}</th>
                  <th className="skill columnheader columndata" data-column="5">{headers[4] || ""}</th>
               </tr>
            </thead>
            <tbody data-page="1">
               {renderedItems.map((a, i) => {
                  // Create isolated values object for this specific adjective
                  const adjectiveIndex = i + 1; // 1-based index
                  const ratingKey = currentSection?.formId + adjectiveIndex + 'r';
                  const categoryKey = currentSection?.formId + adjectiveIndex + 'c';
                  const isHoneypot = isChecklistHoneypotItem(a);
                  
                  const adjectiveValues = {
                     r: values[ratingKey] || 0,
                     c: values[categoryKey] || ''
                  };
                  
                  return (
                     <Adjective
                        key={a.id ?? i}
                        id={a.id}
                        section={currentSection}
                        name={a.name}
                        description={a.description}
                        isAdmin={sectionData?.isAdmin}
                        showValidations={sectionData?.showValidations && !isHoneypot}
                        formId={currentSection?.formId}
                        value={adjectiveValues}
                        setValue={onChange}
                        ratingInput={ratingInput}
                        elements={elements}
                        rowClassName={isHoneypot ? "honeypot" : undefined}
                        tabIndex={isHoneypot ? -1 : undefined}
                     />
                  );
               })}
            </tbody>
         </table>
      </div>
      </div>
   );
}



